/**
 * Session-scoped editor buffer pinning.
 *
 * <DocxEditor> fully reloads (resetForNewDocument + history.reset — wiping PM
 * state, the caret selection, and DOM focus) whenever its `documentBuffer` prop
 * changes identity. That is correct for a genuinely new document, but harmful
 * mid-session: once the user is editing, the editor is the source of truth.
 *
 * React Query hands us a NEW buffer after the first autosave (the draft row is
 * created, so the buffer source flips template→draft and re-fetches the saved
 * blob). Reloading then jumps the caret, drops focus ("I changed the font and
 * now I can't type"), and discards edits made since the autosave snapshot.
 *
 * `resolveSessionBuffer` pins the FIRST buffer resolved for a given
 * (case, template) session and keeps returning it, ignoring later refetches.
 * A session change (different `sessionKey`) drops the pin so the new document
 * loads exactly once.
 */

export interface PinnedBuffer {
  key: string;
  buffer: ArrayBuffer;
}

/**
 * @param prev    the currently pinned buffer (or null on first render / after reset)
 * @param sessionKey identifies the editing session — change it to force a reload
 * @param fetched the latest buffer from the query (undefined while loading)
 * @returns the pin to keep for this render (null = nothing to show yet)
 */
export function resolveSessionBuffer(
  prev: PinnedBuffer | null,
  sessionKey: string,
  fetched: ArrayBuffer | undefined
): PinnedBuffer | null {
  // A different session — forget the previous document's buffer.
  const current = prev && prev.key === sessionKey ? prev : null;
  // Capture the first buffer for this session; ignore every later refetch.
  if (!current && fetched) {
    return { key: sessionKey, buffer: fetched };
  }
  return current;
}

/**
 * Build the session key the pin is scoped to.
 *
 * `reloadNonce` is what makes a conflict recoverable (MDRS-554 / audit RB-8).
 * The pin above is deliberately sticky — it is what stops the editor reloading
 * under the user's caret — but that stickiness is also why a tab never notices
 * the server moved on. Bumping the nonce is the ONE sanctioned way to say
 * "this document is genuinely superseded, load the server's copy", so the
 * conflict dialog's "Reload the latest version" has a mechanism that doesn't
 * involve weakening the pin for every other refetch.
 */
export const buildSessionKey = (
  caseNumber: string | null,
  templateId: string | null,
  reloadNonce = 0
): string => `${caseNumber ?? ""}::${templateId ?? ""}::${reloadNonce}`;
